import * as Sentry from '@sentry/react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { DemoViewerHydrateFallback } from '@/components/DemoViewerHydrateFallback';
import { AuthError } from '@/components/errors/auth';
import { DemoViewerRouteErrorBoundary } from '@/components/errors/demo-viewer';
import { Spinner } from '@/components/ui/spinner';

import {
  FinderRedirect,
  MatchRedirect,
  ScrimMatchRedirect,
  ScrimRedirect,
  UserProfileRedirect,
} from './redirects';
import {
  default as AppRoot,
  ErrorBoundary as AppRootErrorBoundary,
} from './routes/app/root';

const convert =
  (queryClient: QueryClient) =>
  (m: {
    clientLoader?: (queryClient: QueryClient) => unknown;
    clientAction?: (queryClient: QueryClient) => unknown;
    default: React.ComponentType;
    [key: string]: unknown;
  }) => {
    const { clientLoader, clientAction, default: Component, ...rest } = m;
    return {
      ...rest,
      loader: clientLoader?.(queryClient),
      action: clientAction?.(queryClient),
      Component,
    };
  };

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const createAppRouter = (queryClient: QueryClient) =>
  sentryCreateBrowserRouter([
    {
      path: '',
      lazy: async () =>
        import('./routes/landing/landing').then(convert(queryClient)),
      ErrorBoundary: AppRootErrorBoundary,
    },
    {
      path: 'onboarding',
      lazy: async () =>
        import('./routes/onboarding').then(convert(queryClient)),
      ErrorBoundary: AppRootErrorBoundary,
    },
    {
      path: 'onboarding/success',
      lazy: async () =>
        import('./routes/onboarding/complete-step').then(convert(queryClient)),
      ErrorBoundary: AppRootErrorBoundary,
    },
    {
      path: 'register',
      lazy: async () =>
        import('./routes/register/register').then(convert(queryClient)),
      handle: { title: 'Register' },
      ErrorBoundary: AppRootErrorBoundary,
    },
    {
      path: 'connect-steam',
      lazy: async () =>
        import('./routes/register/connect-steam').then(convert(queryClient)),
      handle: { title: 'Connect Steam' },
      ErrorBoundary: AppRootErrorBoundary,
    },
    {
      path: 'cookies',
      lazy: async () =>
        import('./routes/legal/cookies').then(convert(queryClient)),
      handle: { title: 'Cookies' },
      HydrateFallback: () => <Spinner className="mx-auto h-full" />,
    },
    {
      path: 'terms',
      lazy: async () =>
        import('./routes/legal/terms').then(convert(queryClient)),
      handle: { title: 'Terms' },
    },
    {
      path: 'privacy',
      lazy: async () =>
        import('./routes/legal/privacy').then(convert(queryClient)),
      handle: { title: 'Privacy' },
    },
    { path: '/index', element: <Navigate to="/" /> },
    {
      path: '',
      element: <AppRoot />,
      ErrorBoundary: AppRootErrorBoundary,
      children: [
        {
          path: 'scrim-finder',
          lazy: async () =>
            import('./routes/app/scrim-finder').then(convert(queryClient)),
          handle: { title: 'Scrim Finder' },
          children: [
            {
              path: '',
              element: <ScrimRedirect />,
            },
            {
              path: 'find',
              element: <ScrimRedirect />,
            },
            {
              path: 'my-scrims',
              element: <ScrimRedirect />,
            },
          ],
        },
        {
          path: 'scrim/:scrimId',
          lazy: async () =>
            import('./routes/app/scrims/match').then(convert(queryClient)),
          HydrateFallback: () => <Spinner className="mx-auto h-full" />,
        },
        {
          path: 'server',
          lazy: async () =>
            import('./routes/app/server/server').then(convert(queryClient)),
          handle: { title: 'Server' },
          HydrateFallback: () => <Spinner className="mx-auto h-full" />,
          children: [
            {
              path: 'settings',
              handle: { title: 'Server Settings' },
            },
            {
              path: 'configs',
              handle: { title: 'Server Configs' },
            },
          ],
        },
        {
          path: 'team-finder/:category?',
          lazy: async () =>
            import('./routes/app/team-finder/team-finder').then(
              convert(queryClient),
            ),
          handle: { title: 'Team Finder' },
          HydrateFallback: () => <Spinner className="mx-auto h-full" />,
        },
        {
          path: 'analytics',
          lazy: async () =>
            import('./routes/app/analytics/analytics').then(
              convert(queryClient),
            ),
          handle: { title: 'Analytics' },
          HydrateFallback: () => <Spinner className="mx-auto h-full" />,
        },
        {
          path: 'demo-viewer',
          handle: { title: 'Demo Viewer' },
          children: [
            {
              path: '',
              lazy: async () =>
                import('./routes/app/demo').then(convert(queryClient)),
              HydrateFallback: () => <Spinner className="mx-auto h-full" />,
            },
            {
              path: 'multi-game/:selectionId?',
              lazy: async () =>
                import('./routes/app/demo/multi-game-viewer').then(
                  convert(queryClient),
                ),
              HydrateFallback: () => <DemoViewerHydrateFallback />,
              ErrorBoundary: DemoViewerRouteErrorBoundary,
              handle: { hideHeader: true },
            },
            {
              path: ':mapId',
              lazy: async () =>
                import('./routes/app/demo/demo-viewer').then(
                  convert(queryClient),
                ),
              HydrateFallback: () => <DemoViewerHydrateFallback />,
              ErrorBoundary: DemoViewerRouteErrorBoundary,
              handle: { hideHeader: true },
            },
          ],
        },
        {
          path: 'profile/:id?',
          lazy: async () =>
            import('./routes/app/profile').then(convert(queryClient)),
          handle: { title: 'Profile' },
          HydrateFallback: () => <Spinner className="mx-auto h-full" />,
        },
        {
          path: 'profilesettings',
          lazy: async () =>
            import('./routes/app/profile/settings').then(convert(queryClient)),
          handle: { title: 'Profile Settings' },
          HydrateFallback: () => <Spinner className="mx-auto h-full" />,
        },
        {
          path: 'team/:id',
          lazy: async () =>
            import('./routes/app/team').then(convert(queryClient)),
          handle: { title: 'Team' },
          HydrateFallback: () => <Spinner className="mx-auto h-full" />,
        },
        {
          path: 'match/:id',
          lazy: async () =>
            import('./routes/app/league/match').then(convert(queryClient)),
          handle: { title: 'Match' },
          HydrateFallback: () => <Spinner className="mx-auto h-full" />,
        },
        {
          path: 'league/:season?/:division?',
          lazy: async () =>
            import('./routes/app/league/league').then(convert(queryClient)),
          handle: { title: 'League' },
          HydrateFallback: () => <Spinner className="mx-auto h-full" />,
        },
        {
          path: 'article/:id?',
          lazy: async () =>
            import('./routes/app/article/article').then(convert(queryClient)),
          handle: { title: 'Article' },
          HydrateFallback: () => <Spinner className="mx-auto h-full" />,
        },
        {
          path: 'worldranking',
          lazy: async () =>
            import('./routes/app/world-ranking/world-ranking').then(
              convert(queryClient),
            ),
          handle: { title: 'World Ranking' },
          HydrateFallback: () => <Spinner className="mx-auto h-full" />,
        },

        {
          path: 'leaguerules',
          lazy: async () =>
            import('./routes/app/league/rules').then(convert(queryClient)),
          handle: { title: 'League Rules' },
          HydrateFallback: () => <Spinner className="mx-auto h-full" />,
        },
      ],
    },
    // // Redirect routes
    { path: '/userprofile/:id', element: <UserProfileRedirect /> },
    { path: '/league_match/:id', element: <MatchRedirect /> },
    { path: '/matchscore/:id', element: <MatchRedirect /> },
    { path: '/scrim-finder/:id', element: <ScrimMatchRedirect /> },
    { path: '/scrim/*', element: <ScrimRedirect /> },
    { path: '/scrim/find', element: <ScrimRedirect /> },
    { path: '/finder/*', element: <FinderRedirect /> },
    {
      path: '/auth/error',
      element: <AuthError />,
    },
    {
      path: '*',
      lazy: async () =>
        import('./routes/not-found/not-found').then(convert(queryClient)),
    },
  ]);

export const AppRouter = () => {
  const queryClient = useQueryClient();

  const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

  return <RouterProvider router={router} />;
};
